import { FieldLabel, FlexBox, Icon, Text } from '@dixa/lib-shared-habla';
import { ArticleDataContext } from 'articleEditor/contexts/articleDataProvider';
import { useIsTranslating } from 'articleEditor/hooks/translations/useIsTranslating';
import { useCollectionId } from 'articleEditor/hooks/useCollectionId';
import CategoryDropdown from 'lib-knowledge-management-common/components/categoryTreeInput/categoryDropdown';
import { ParentRef } from 'lib-knowledge-management-common/models/types';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useContext, useEffect } from 'react';
import { useSnapshot } from 'valtio';

import useArticleMove from 'hooks/useArticleMove';

import s from './settings.module.scss';
import { articleSettingsActions, articleSettingsState } from './settings.store';
import { Tags } from './tags';

export function Settings(): JSX.Element {
  const { categoryTouched } = useSnapshot(articleSettingsState);
  const collectionId = useCollectionId();
  const { id: articleId } = useContext(ArticleDataContext);
  const { errorNotify } = useNotification();
  const isTranslating = useIsTranslating();

  const [move, { loading: categoryChangeLoading, error: categoryChangeError }] =
    useArticleMove();

  useEffect(() => {
    if (!categoryChangeError) return;
    errorNotify({
      text: 'Changing category failed',
    });
  }, [categoryChangeError, errorNotify]);

  const articleData = useContext(ArticleDataContext);

  const onChange = (newParentRef: ParentRef) => {
    move({
      articleId,
      newCategoryId: newParentRef.id,
    });
  };

  const category =
    articleData?.parent && articleData.parent.__typename === 'Category'
      ? articleData.parent
      : undefined;
  const tags = articleData?.tags || [];
  const isSyncedWithElevio = articleData?.isSyncedWithElevio;

  return (
    <FlexBox flexDirection="column" gap="4px">
      <FlexBox gap="16px">
        <FieldLabel className={s['settingsLabel']}>
          <Icon className={s['settingsIcon']} name="folder" />
          <Text.Paragraph variant="2" subdued>
            Category
          </Text.Paragraph>
        </FieldLabel>
        {collectionId && (
          <CategoryDropdown
            collectionId={collectionId}
            className={s['settingsSelect']}
            value={category}
            onChange={onChange}
            withCollectionOption={false}
            currentCategoryId={undefined}
            disabled={
              categoryChangeLoading || isSyncedWithElevio || isTranslating
            }
            required
            onToggle={(opened) => {
              !opened && articleSettingsActions.blurCategoryDropdown();
            }}
            touched={categoryTouched}
            placeholder={
              isSyncedWithElevio
                ? 'Detach article to change category...'
                : 'Add category...'
            }
          />
        )}
      </FlexBox>
      <Tags tags={tags} articleId={articleId} />
    </FlexBox>
  );
}
